<template>
  <sharing-metadata
    :metadata-props="youtubeMetadata"
    @update="$emit('update', $event)"
    @invalid="$emit('invalid')"
  >
    <template v-slot:header>
      <slot name="header">YouTube metadata</slot>
    </template>
    <template v-slot:content="contentData"
      ><div class="metadata-row">
        <el-form-item label="Title" class="youtube-title" prop="title">
          <el-input v-model="contentData.metadata.title" clearable> </el-input>
        </el-form-item>
        <el-form-item
          label="Category"
          class="youtube-category"
          prop="youtubeCategory"
        >
          <el-select
            v-model="contentData.metadata.categoryId"
            placeholder="Select category"
          >
            <el-option
              v-for="(cat, index) in $options.youtubeCategories"
              :key="index"
              :label="cat"
              :value="index"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Access" class="youtube-privacy" prop="isPublic">
          <el-checkbox-button
            v-model="contentData.metadata.isPublic"
            class="youtube-public__button"
          >
            <span v-if="contentData.metadata.isPublic">
              Public
            </span>
            <span v-else>
              Private
            </span>
          </el-checkbox-button>
        </el-form-item>
      </div>
      <el-form-item label="Description" prop="description">
        <el-input
          v-model="contentData.metadata.description"
          type="textarea"
          class="youtube-description__input"
          :rows="4"
        >
        </el-input>
      </el-form-item>
      <el-form-item
        label="Playlist (optional)"
        class="youtube-category"
        style="margin-top: 10px"
      >
        <el-select
          v-model="contentData.metadata.playlistId"
          placeholder="Select playlist"
        >
          <el-option label="No playlist" :value="null" />
          <el-option
            v-for="playlist in playlists"
            :key="playlist.id"
            :label="playlist.title"
            :value="playlist.id"
          />
        </el-select>
      </el-form-item>
      <div class="tag-input">
        <el-form-item label="Tags" :error="newTagError">
          <el-input
            v-if="totalTagsLength < maxTagsTotalLength"
            v-model.trim="newTag"
            type="text"
            :maxlength="newTagError ? null : restTagLength"
            show-word-limit
            placeholder="Enter a Tag"
            class="tag-input__text"
            @keydown.enter.native="addTag"
            @keydown.188.native="addTag"
          >
          </el-input>
        </el-form-item>
        <div
          v-for="(tag, index) in contentData.metadata.tags"
          :key="tag"
          class="tag-input__tag"
        >
          {{ tag }}
          <span @click="removeTag(index)">x</span>
        </div>
      </div>
    </template>
  </sharing-metadata>
</template>

<script>
import SharingMetadata from "@/components/socials/share/metadata/SharingMetadata.vue";

import { YOUTUBE_CATEGORIES } from "@/api/socials/youtube.js";
// @group Socials
// YouTube sharing additional data (title, category, description, public, tags) input
export default {
  name: "SharingYoutubeMetadata",
  components: {
    SharingMetadata
  },
  props: {
    /** YouTube video title  */
    title: {
      type: String,
      default: "No title"
    },
    /** YouTube video category ID  */
    categoryId: {
      type: String,
      default: null
    },
    /** YouTube video description  */
    description: {
      type: String,
      default: ""
    },
    /** YouTube video public option  */
    isPublic: {
      type: Boolean,
      default: false
    },
    /** YouTube video tags array  */
    tags: {
      type: Array,
      default: () => []
    },
    /** Playlist identifier */
    playlistId: {
      type: String,
      default: null
    },
    /** All channel playlists */
    playlists: {
      type: Array,
      default: () => []
    }
  },
  /** YouTube cateries map (object) in format <ID: name> */
  youtubeCategories: YOUTUBE_CATEGORIES,
  data() {
    return {
      newTag: "",
      newTagError: null,
      /** maximium length of all tags */
      maxTagsTotalLength: 500,
      /** maximum length of one tag */
      maxTagLength: 100
    };
  },
  computed: {
    youtubeMetadata() {
      return [
        {
          id: "title",
          value: this.title,
          rules: [
            {
              required: true,
              message: "Title is required",
              trigger: "change"
            },
            {
              max: 70,
              message: "Max title length is 70",
              trigger: "change"
            }
          ]
        },
        {
          id: "categoryId",
          value: this.categoryId
        },
        {
          id: "description",
          value: this.description,
          rules: [
            {
              max: 1000,
              message: "Max description length is 1000",
              trigger: "change"
            }
          ]
        },
        {
          id: "isPublic",
          value: this.isPublic
        },
        {
          id: "tags",
          value: this.tags
        },
        {
          id: "playlistId",
          value: this.playlistId
        }
      ];
    },
    youtubeProps() {
      return Object.fromEntries(
        this.youtubeMetadata.map(({ id, value }) => [id, value])
      );
    },
    // @vuese
    // Length of all YouTube tags with commas
    totalTagsLength() {
      return this.tags.reduce((length, tag) => length + tag.length + 1, 0);
    },
    // @vuese
    // Available count of YouTube tag characters (YouTube have restriction for full tags length)
    restTagLength() {
      const tagsLength = this.totalTagsLength;
      return Math.min(this.maxTagsTotalLength - tagsLength, this.maxTagLength);
    }
  },
  watch: {
    // @vuese
    // Check errors in new tag on change
    newTag() {
      if (this.newTag !== "") {
        this.validateNewTag();
      }
    },
    // @vuese
    // Check errors in new tag on tags list change
    tags() {
      if (this.newTag !== "") {
        this.validateNewTag();
      }
    }
  },
  methods: {
    updateMetadata(newData) {
      if ("newTag" in newData) {
        this.newTag = newData.newTag;
      }
      this.$emit("update", newData);
    },
    // @vuese
    // Add new YouTube tag into sharing form
    addTag(event) {
      event.preventDefault();

      this.validateNewTag();

      if (this.newTagError) return;

      this.updateMetadata({
        ...this.youtubeProps,
        tags: [this.newTag, ...this.tags],
        newTag: ""
      });
    },
    // @vuese
    // Remove YouTube tag from sharing form
    // @arg idx - index in tags list
    removeTag(idx) {
      const newTags = [...this.tags];
      newTags.splice(idx, 1);
      this.updateMetadata({ ...this.youtubeProps, tags: newTags });
    },
    // @vuese
    // Check, that there is not the same tag in tags
    // @arg tag - tag value
    checkTagUnique(tag) {
      return !this.tags.includes(tag);
    },
    // @vuese
    // Check errors in new tag and set/reset error state
    validateNewTag() {
      if (this.newTag.length < 2) {
        this.newTagError = "Tag is too short";
        return;
      }

      if (!this.checkTagUnique(this.newTag)) {
        this.newTagError = "Tag is added";
        return;
      }

      this.newTagError = null;
    }
  }
};
</script>
<style >
.youtube-description__input{
  background-color: #aaaaaa;
}

.el-checkbox-button:first-child .el-checkbox-button__inner{
  background-color: #ed5229;
}
</style>